<template>
    <teleport to="body">
        <transition leave-active-class="duration-200">
            <div
                v-show="show"
                class="fixed inset-0 overflow-y-auto px-4 py-6 sm:px-0 z-50"
            >
                <transition
                    enter-active-class="ease-out duration-300"
                    enter-from-class="opacity-0"
                    enter-to-class="opacity-100"
                    leave-active-class="ease-in duration-200"
                    leave-from-class="opacity-100"
                    leave-to-class="opacity-0"
                >
                    <div
                        v-show="show"
                        class="fixed inset-0 transform transition-all"
                        @click="close"
                    >
                        <div class="absolute inset-0 bg-gray-500 opacity-75" />
                    </div>
                </transition>

                <transition
                    enter-active-class="ease-out duration-300"
                    enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enter-to-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-active-class="ease-in duration-200"
                    leave-from-class="opacity-100 translate-y-0 sm:scale-100"
                    leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div
                        v-show="show"
                        class="shadow-xl transform transition-all rounded-lg max-w-5xl mx-auto overflow-hidden"
                    >
                        <main class="flex flex-col space-y-3 bg-white p-5">
                          <h3 class="text-lg font-semibold">Flight times</h3>
                          <div v-for="(flight, index) in selected" class="mt-1 flex items-center space-x-3">
                            <input type="text" v-model="selected[index].origin" placeholder="Origin" :name="'flight-' + index + '-origin'" :id="'flight-' + index + '-origin'" class="block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                            <input type="text" v-model="selected[index].destination" placeholder="Destination" :name="'flight-' + index + '-destination'" :id="'flight-' + index + '-destination'" class="block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                            <input type="text" v-model="selected[index].agency" placeholder="Agency" :name="'flight-' + index + '-agency'" :id="'flight-' + index + '-agency'" class="block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                            <datepicker v-model="selected[index].departure" :name="'flight-' + index + '-departure'" :id="'flight-' + index + '-departure'" class="block rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" />
                            <svg @click="removeFlight(index)" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6 cursor-pointer text-gray-400 hover:text-gray-100">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <button @click="addFlight" class="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-gray-300 px-3 py-1 mt-4 text-sm font-medium leading-4 text-gray-900 shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span>
                              Add flight
                            </span>
                          </button>
                          <div class="flex justify-end space-x-3">
                            <button @click="cancel" type="button" class="inline-flex items-center justify-center rounded-md border border-transparent bg-red-300 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:w-auto">Cancel</button>
                            <button @click="close" type="button" class="inline-flex items-center justify-center rounded-md border border-transparent bg-gray-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:w-auto">Close</button>
                            <button @click="save" type="button" class="inline-flex items-center justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:w-auto">Save</button>
                          </div>
                        </main>
                    </div>
                </transition>
            </div>
        </transition>
    </teleport>
</template>

<script setup>
import {ref, onMounted, onUnmounted} from 'vue'
import {useProposalOldStore} from "@/Stores/ProposalOld";
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

const store = useProposalOldStore();
const selected = ref(null);

onMounted(() => {
  resetSelected();
  document.addEventListener('keydown', closeOnEscape)
});
onUnmounted(() => document.removeEventListener('keydown', closeOnEscape));

const emit = defineEmits({
    close: (_value) => true,
    save: (_value) => _value
});

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  }
})

const closeOnEscape = (e) => {
    if (e.key === 'Escape' && props.show) {
        close();
    }
};

const close = () => {
    emit('close');
};

const cancel = () => {
  emit('close');
  resetSelected();
};

const save = () => {
    emit('save', selected.value);
};

function removeFlight(index) {
  selected.value.splice(index, 1);
}
function addFlight() {
  selected.value.push({
    origin: '',
    destination: '',
    agency: '',
    departure: new Date(store.proposal.start_date + ' 12:00:00')
  });
}

function resetSelected() {
  selected.value = (store.proposal.data === null || typeof store.proposal.data.flights === 'undefined') ? [] : JSON.parse(JSON.stringify(store.proposal.data.flights));
}

</script>
